import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'

type WebhookEventType =
  | 'email.queued'
  | 'email.sent'
  | 'email.delivered'
  | 'email.bounced'
  | 'email.complained'
  | 'email.opened'
  | 'email.clicked'
  | 'email.rejected'
  | 'email.delayed'
  | 'email.spam_trap'

interface Webhook {
  id: string
  url: string
  events: WebhookEventType[]
  active: boolean
  description?: string
  secret: string
  last_delivery_at: string | null
  created_at: string
}

interface WebhookListResponse {
  data: Webhook[]
}

interface CreateWebhookRequest {
  url: string
  events: WebhookEventType[]
  description?: string
}

interface UpdateWebhookRequest {
  url?: string
  events?: WebhookEventType[]
  active?: boolean
  description?: string
}

interface WebhookTestResult {
  success: boolean
  status_code: number
  response_time_ms: number
  error?: string
}

interface WebhookLog {
  id: string
  event_id: string
  status_code: number
  response_body: string | null
  attempt: number
  created_at: string
  duration_ms: number
}

interface WebhookLogListResponse {
  data: WebhookLog[]
}

const QUERY_KEY = ['webhooks'] as const

export function useWebhooks(): ReturnType<typeof useQuery<WebhookListResponse>> {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => apiClient.get<WebhookListResponse>(endpoints.webhooks.list),
  })
}

export function useWebhookLogs(id: string): ReturnType<typeof useQuery<WebhookLogListResponse>> {
  return useQuery({
    queryKey: [...QUERY_KEY, id, 'logs'],
    queryFn: () => apiClient.get<WebhookLogListResponse>(endpoints.webhooks.logs(id)),
    enabled: !!id,
  })
}

export function useCreateWebhook(): ReturnType<
  typeof useMutation<Webhook, unknown, CreateWebhookRequest>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateWebhookRequest) =>
      apiClient.post<Webhook>(endpoints.webhooks.create, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

export function useUpdateWebhook(): ReturnType<
  typeof useMutation<Webhook, unknown, { id: string; data: UpdateWebhookRequest }>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateWebhookRequest }) =>
      apiClient.put<Webhook>(endpoints.webhooks.update(id), data),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

export function useDeleteWebhook(): ReturnType<typeof useMutation<unknown, unknown, string>> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(endpoints.webhooks.delete(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

export function useTestWebhook(): ReturnType<
  typeof useMutation<WebhookTestResult, unknown, string>
> {
  return useMutation({
    mutationFn: (id: string) => apiClient.post<WebhookTestResult>(endpoints.webhooks.test(id), {}),
  })
}

export type {
  CreateWebhookRequest,
  UpdateWebhookRequest,
  Webhook,
  WebhookEventType,
  WebhookLog,
  WebhookTestResult,
}
